import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Flex, Box, Center, CircularProgress, Text } from '@chakra-ui/react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Prompt } from 'react-router';
import { ConfirmModal, SplitPaneLayout, InfoModal } from '../';
import VideoSession from './videoSession';
import TextSession from './textSession';
import ParticipantsPane from './participantPane';
import ParticipantAudio from './participantAudio';
import Chat from './chat';
import {
  SessionType,
  initSession,
  addNewMember,
  removeMember,
  removeMembers,
  clearScreens,
  handleRoomDisconnected,
  openModal,
  closeModal,
  Modals,
} from '../../redux/reducers';
import { connectRoom, disconnectRoom, createTracks } from '../../twilio/twilio';
import { useRoom, useDataTrackMessages } from '../../twilio/hooks';
import { handleAdminMessage } from '../../twilio/adminCommands';
import { TwilioRoomEvents } from '../../twilio/constants';
import { useTranslation } from 'react-i18next';
import { isChatAdmin } from '../../utils/user/user';
import { updateParticipantSubscriptions } from '../../utils/session/session';
import { InfoModalMode } from '../common/modal/infoModal';
import SettingsModal from '../common/modal/settingsModal';

const BrowserEvents = Object.freeze({
  PageHide: 'pagehide',
  BeforeUnload: 'beforeunload',
});

// pick only some props, use with shallowEqual
const sessionSelector = ({ activeSession: { members, screens, type } }) => ({
  members,
  screens,
  type,
});

const adminTrackFilter = (track) => track.name === 'admin';

const SessionPage = ({ match, history }) => {
  const dispatch = useDispatch();
  const sessionId = match.params.sessionId;
  const { activeSessionData, initializingSession, sessionInitError } = useSelector(
    (state) => state.session
  );
  const { userData } = useSelector((state) => state.user);
  const room = useRoom();
  const { members, screens, type: sessionType } = useSelector(sessionSelector, shallowEqual);
  const { t } = useTranslation();
  const { showModal, modalProps, modalName } = useSelector((state) => state.ui);
  const { micInUse, cameraInUse } = useSelector((state) => state.settings);

  useEffect(() => {
    // if modal is open from from previous session or other page
    if (showModal) {
      dispatch(closeModal());
      closeModal();
    }
  }, []);

  useEffect(() => {
    if (room != null) {
      // clears the room
      const cleanUpRoom = (event) => {
        if (event.persisted) {
          return;
        }
        dispatch(disconnectRoom());
      };

      // listen events
      window.addEventListener(BrowserEvents.PageHide, cleanUpRoom);
      window.addEventListener(BrowserEvents.BeforeUnload, cleanUpRoom);

      return () => {
        // remove listeners
        window.removeEventListener(BrowserEvents.PageHide, cleanUpRoom);
        window.removeEventListener(BrowserEvents.BeforeUnload, cleanUpRoom);
        dispatch(disconnectRoom());
      };
    }
  }, [room]);

  const onParticipantConnected = (twilioParticipant, isSelf) => {
    // add new member
    dispatch(addNewMember(twilioParticipant, isSelf));
  };

  const onParticipantDisconnected = (twilioParticipant) => {
    dispatch(removeMember(twilioParticipant));
  };

  const doConnectRoom = useCallback(
    async ({
      // username,
      roomName,
      sessionType,
      accessToken,
      sessionId,
      ownRole,
    }) => {
      try {
        const createVideoTrack =
          sessionType !== SessionType.VIDEO ? null : cameraInUse ? 'enabled' : 'disabled';
        const createAudioTrack = micInUse ? 'enabled' : 'disabled';
        const dataTracks = ['text', 'all'];
        if (isChatAdmin(userData)) {
          dataTracks.push('admin');
        }
        const localTracks = await dispatch(
          createTracks(createVideoTrack, createAudioTrack, dataTracks)
        );
        const disconnectHandler = async (room, error) => {
          dispatch(handleRoomDisconnected(room, error, () => doConnect()));
        };

        const room = await dispatch(
          connectRoom(roomName, localTracks, accessToken, disconnectHandler)
        );

        const audioTracks = room.localParticipant.audioTracks;
        for (const [, publication] of audioTracks) {
          publication.setPriority('low');
        }

        if (ownRole === 'hears') {
          const videoTracks = room.localParticipant.videoTracks;
          const priority = ownRole === 'hears' ? 'low' : 'standard'; // 'high'
          for (const [, publication] of videoTracks) {
            publication.setPriority(priority);
          }
        }

        // add self
        onParticipantConnected(room.localParticipant, true);

        // get current joined participants
        room.participants.forEach((participant) => onParticipantConnected(participant, false));

        // listen changes
        room.on(TwilioRoomEvents.ParticipantConnected, (participant) =>
          onParticipantConnected(participant, false)
        );

        room.on(TwilioRoomEvents.ParticipantDisconnected, (participant) =>
          onParticipantDisconnected(participant)
        );

        updateParticipantSubscriptions(room.localParticipant.identity, sessionId).catch((error) => {
          console.error('failed to update subscriptions', error);
        });
      } catch (error) {
        // show error modal
        // DOMException: Requested device not found
        // DOMException: Permission denied
        // DOMException: Access token expired or expiration date invalid

        console.error('connectRoom - connect error', error);

        let errorMessage =
          (error.code || '') +
          ' ' +
          (typeof error.toString === 'function' ? error.toString() : error.message || error);

        // customize error message, if needed
        switch (error.name) {
          case 'NotAllowedError':
            // errorMessage = t('modal.accessDenied');
            break;
        }

        dispatch(
          openModal(
            {
              headerText: { i18nKey: 'modal.connectionError' },
              bodyText: <p>{errorMessage}</p>,
            },
            Modals.CONNECTION_ERROR
          )
        );
      }
    },
    [dispatch, t]
  );

  const doConnect = useCallback(async () => {
    if (sessionId === null || userData === null) {
      return;
    }

    const { username } = userData;
    console.log('sessionId', sessionId);
    console.log('userData', userData);

    // initialize (user type is determined in the redux side)
    let activeSessionData;
    try {
      activeSessionData = await dispatch(initSession(userData, sessionId));
    } catch (error) {
      // initSession(...) sets error state
      return;
    }

    console.log('activeSessionData', activeSessionData);
    const {
      callData: { accessToken, roomName, roles },
      session,
    } = activeSessionData;

    const ownRole = (roles || {})[username];
    await doConnectRoom({
      username,
      roomName,
      sessionType: session.type,
      accessToken,
      ownRole,
      sessionId,
    });
  }, [userData, sessionId, doConnectRoom]);

  useEffect(() => {
    doConnect();
  }, [doConnect]);

  const onEndSession = () => {
    dispatch(clearScreens());
    dispatch(removeMembers());
    dispatch(disconnectRoom());
    history.goBack();
  };

  const doCloseModal = useCallback(() => {
    dispatch(closeModal());
  }, [dispatch]);

  const retryConnect = useCallback(() => {
    dispatch(closeModal());
    doConnect();
  }, [dispatch]);

  const messageHandler = useCallback(
    (message) => {
      dispatch(handleAdminMessage(message));
    },
    [dispatch]
  );
  useDataTrackMessages(adminTrackFilter, messageHandler);

  const membersOnPane =
    sessionType !== SessionType.VIDEO
      ? members
      : members.filter((m) => !screens.find((s) => s.member && s.member.id === m.id));

  const renderSession = () => {
    if (activeSessionData == null || initializingSession === true || sessionInitError != null) {
      return null;
    }

    return (
      <>
        <Prompt when={room && room.state !== 'disconnected'} message={t('modal.leaveSession')} />
        <InfoModal
          open={showModal === true && modalName === Modals.CONNECTION_ERROR}
          mode={InfoModalMode.Error}
          leftButtonText={null}
          rightButtonText={t('modal.retry')}
          onClose={doCloseModal}
          onRightButtonClick={retryConnect}
          {...modalProps}
        />
        <SettingsModal open={showModal === true && modalName === Modals.SETTINGS} {...modalProps} />
        <ConfirmModal
          open={showModal === true && modalName === Modals.CONFIRM}
          bodyTextProps={{ mt: 2, mb: 10 }}
          onClose={doCloseModal}
          {...modalProps}
        />

        <SplitPaneLayout footerComponents={{ onEndSession }}>
          <Flex height="100%" flexDir="column">
            <Box height="84%">
              {sessionType === SessionType.TEXT && <TextSession height="100%" />}
              {sessionType === SessionType.VIDEO && (
                <VideoSession height="100%" screens={screens} />
              )}
            </Box>
            <Box height="16%">
              <ParticipantsPane members={membersOnPane} />
            </Box>
            <Flex>
              {members.map((member) => (
                <ParticipantAudio key={member.id} member={member} />
              ))}
            </Flex>
          </Flex>
          <Box bg="brand.10" height="100%">
            <Chat members={members} room={room} />
          </Box>
        </SplitPaneLayout>
      </>
    );
  };

  const renderInitSession = () => {
    if (initializingSession === true || sessionInitError != null) {
      return (
        <Center height="100vh" width="100%">
          <Text>{t('joiningSession')}</Text>
          {initializingSession === true && <CircularProgress isIndeterminate size={20} />}
          {sessionInitError != null && (
            <Text>{`${t('sessionJoiningFailed')} ${sessionInitError.status || ''}`}</Text>
          )}
        </Center>
      );
    }
  };

  return (
    <Box>
      {renderSession()}
      {renderInitSession()}
    </Box>
  );
};

SessionPage.propTypes = {
  session: PropTypes.object,
  match: PropTypes.object,
  history: PropTypes.object,
};

export default SessionPage;
